import React, { useEffect, useRef } from "react";

const CompanyProfile = () => {
  const sectionRef = useRef(null);
  const highlightsRef = useRef([]);
  const logoRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");

            // Animate highlights sequentially
            highlightsRef.current.forEach((li, index) => {
              setTimeout(() => {
                if (li) li.classList.add("in-view");
              }, index * 200);
            });

            // Animate logo
            setTimeout(() => {
              if (logoRef.current) logoRef.current.classList.add("in-view");
            }, 600);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const setHighlightRef = (el, index) => {
    highlightsRef.current[index] = el;
  };

  return (
    <section id="company-profile" className="company-profile" ref={sectionRef}>
      <div className="container">
        <div className="profile-grid">
          {/* LEFT CONTENT */}
          <div className="profile-content">
            <div className="title-wrapper">
              <h2 className="title">
                <span className="gradient-text">Codriva Tech</span>
                <div className="title-underline"></div>
              </h2>
            </div>
            <h2> <span>Codriva Tech</span> </h2>
            <p className="subtitle">
              Codriva Tech is a modern IT solutions company focused on
              building scalable, secure, and innovative digital products.
            </p>

            <p className="description">
              We specialize in delivering high-quality web applications,
              mobile apps, custom software, and cloud-based solutions.
              Our mission is to empower businesses with smart technology
              that drives growth, efficiency, and long-term success.
            </p>

            <ul className="highlights">
              {[
                "Custom Web & Mobile Development",
                "Scalable Software Solutions",
                "UI/UX Focused Design",
                "Reliable IT Support & Maintenance",
                "Cloud Architecture & DevOps",
                "AI & Machine Learning Integration"
              ].map((item, index) => (
                <li
                  key={index}
                  ref={(el) => setHighlightRef(el, index)}
                  className="highlight-item"
                >
                  <span className="check-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M7 10L9 12L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <div className="stats">
              <div className="stat-item">
                <span className="stat-number">100+</span>
                <span className="stat-label">Projects Delivered</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">98%</span>
                <span className="stat-label">Client Satisfaction</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">3+</span>
                <span className="stat-label">Years Experience</span>
              </div>
            </div>
          </div>

          {/* RIGHT LOGO & VISUAL */}
          <div className="profile-visual">
            <div className="logo-container" ref={logoRef}>
              <div className="logo-glow"></div>
              <img
                src="/companylogo.png"
                alt="Codriva Tech Logo"
                className="logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.querySelector('.logo-fallback').style.display = 'block';
                }}
              />
              {/* <div className="logo-fallback">
                <div className="logo-text">CT</div>
              </div> */}
            </div>

            <div className="tech-elements">
              <div className="circle circle-1"></div>
              <div className="circle circle-2"></div>
              <div className="circle circle-3"></div>
              <div className="particle particle-1"></div>
              <div className="particle particle-2"></div>
              <div className="particle particle-3"></div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .company-profile {
          padding: 60px 0;
          background: linear-gradient(135deg, 
            #020617 0%, 
            #0f172a 30%, 
            #1e293b 100%);
          color: #f8fafc;
          position: relative;
          overflow: hidden;
        }

        .company-profile::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(34, 211, 238, 0.3), 
            transparent);
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 32px;
          position: relative;
          z-index: 2;
        }

        .profile-grid {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          align-items: center;
          gap: 80px;
        }

        /* Title Styles */
        .title-wrapper {
          margin-bottom: 32px;
        }

        .title {
          font-size: 3.5rem;
          font-weight: 800;
          margin-bottom: 12px;
          position: relative;
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .title.in-view {
          opacity: 1;
          transform: translateY(0);
        }

        .gradient-text {
          background: linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .title-underline {
          width: 100px;
          height: 4px;
          background: linear-gradient(90deg, #22d3ee, #3b82f6);
          border-radius: 2px;
          margin-top: 12px;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 1s ease 0.3s;
        }
  .profile-content h2 {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 22px;
        }

  .profile-content h2 span {
          color: #22d3ee;
          text-shadow: 0 0 20px rgba(34, 211, 238, 0.6);
        }
        .title.in-view .title-underline {
          transform: scaleX(1);
        }

        /* Content Styles */
        .subtitle {
          font-size: 1.4rem;
          line-height: 1.6;
          margin-bottom: 24px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s;
          color: #cbd5e1;
        }

        .company-profile.in-view .subtitle {
          opacity: 1;
          transform: translateY(0);
        }

        .description {
          font-size: 1.15rem;
          line-height: 1.8;
          margin-bottom: 40px;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s ease 0.4s, transform 0.8s ease 0.4s;
          color: #94a3b8;
        }

        .company-profile.in-view .description {
          opacity: 1;
          transform: translateY(0);
        }

        /* Highlights */
        .highlights {
          list-style: none;
          padding: 0;
          margin: 0 0 48px 0;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        @media (max-width: 768px) {
          .highlights {
            grid-template-columns: 1fr;
          }
        }

        .highlight-item {
          font-size: 1.05rem;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 0;
          opacity: 0;
          transform: translateX(-20px);
          transition: opacity 0.5s ease, transform 0.5s ease, color 0.3s ease;
          color: #cbd5e1;
        }

        .highlight-item.in-view {
          opacity: 1;
          transform: translateX(0);
        }

        .highlight-item:hover {
          color: #22d3ee;
        }

        .highlight-item:hover .check-icon {
          transform: scale(1.1);
          color: #22d3ee;
        }

        .check-icon {
          color: #3b82f6;
          transition: transform 0.3s ease, color 0.3s ease;
          flex-shrink: 0;
        }

        /* Stats */
        .stats {
          display: flex;
          gap: 40px;
          padding-top: 32px;
          border-top: 1px solid rgba(148, 163, 184, 0.1);
        }

        .stat-item {
          display: flex;
          flex-direction: column;
        }

        .stat-number {
          font-size: 2.2rem;
          font-weight: 700;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #94a3b8;
          margin-top: 4px;
        }

        /* Visual Section */
        .profile-visual {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .logo-container {
          position: relative;
          width: 380px;
          height: 380px;
          display: flex;
          justify-content: center;
          align-items: center;
          opacity: 0;
          transform: scale(0.8);
          transition: opacity 1s ease 0.6s, transform 1s ease 0.6s;
        }

        .logo-container.in-view {
          opacity: 1;
          transform: scale(1);
        }

        .logo-glow {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 24px;
          background: radial-gradient(circle at center, 
            rgba(34, 211, 238, 0.1) 0%,
            rgba(34, 211, 238, 0.05) 40%,
            transparent 70%);
          animation: pulse 4s ease-in-out infinite;
        }

        .logo-image {
          max-width: 100%;
          max-height: 100%;
          border-radius: 20px;
          box-shadow: 
            0 0 60px rgba(34, 211, 238, 0.2),
            0 0 100px rgba(34, 211, 238, 0.1);
          position: relative;
          z-index: 2;
          // animation: float 6s ease-in-out infinite;
          transition: transform 0.3s ease;
        }

        .logo-image:hover {
          transform: translateY(-5px);
        }

        .logo-fallback {
          display: none;
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, #0f172a, #1e293b);
          border-radius: 20px;
          display: flex;
          justify-content: center;
          align-items: center;
          box-shadow: 
            0 0 60px rgba(34, 211, 238, 0.2),
            0 0 100px rgba(34, 211, 238, 0.1);
        }

        .logo-text {
          font-size: 4rem;
          font-weight: 800;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Tech Elements */
        .tech-elements {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          z-index: 1;
        }

        .circle {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(34, 211, 238, 0.1);
          animation: rotate 20s linear infinite;
        }

        .circle-1 {
          width: 500px;
          height: 500px;
          top: -60px;
          left: -60px;
          animation-delay: 0s;
        }

        .circle-2 {
          width: 400px;
          height: 400px;
          top: 40px;
          right: -40px;
          animation-delay: -10s;
          animation-direction: reverse;
        }

        .circle-3 {
          width: 300px;
          height: 300px;
          bottom: -30px;
          left: 50%;
          margin-left: -150px;
        }

        .particle {
          position: absolute;
          width: 6px;
          height: 6px;
          background: #22d3ee;
          border-radius: 50%;
          filter: blur(1px);
          animation: particle-float 8s ease-in-out infinite;
        }

        .particle-1 {
          top: 20%;
          left: 20%;
          animation-delay: 0s;
        }

        .particle-2 {
          top: 60%;
          right: 30%;
          animation-delay: -2s;
        }

        .particle-3 {
          bottom: 30%;
          left: 40%;
          animation-delay: -4s;
        }

        /* Animations */
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(1deg); }
          66% { transform: translateY(5px) rotate(-1deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes particle-float {
          0%, 100% { 
            transform: translateY(0) translateX(0);
            opacity: 0.3;
          }
          33% { 
            transform: translateY(-20px) translateX(10px);
            opacity: 0.6;
          }
          66% { 
            transform: translateY(10px) translateX(-10px);
            opacity: 0.4;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 1024px) {
          .profile-grid {
            grid-template-columns: 1fr;
            gap: 60px;
            text-align: center;
          }

          .logo-container {
            width: 300px;
            height: 300px;
            margin: 0 auto;
          }

          .highlights {
            text-align: left;
          }

          .stats {
            justify-content: center;
            flex-wrap: wrap;
          }

          .title {
            font-size: 2.8rem;
          }

          .subtitle {
            font-size: 1.2rem;
          }
        }

        @media (max-width: 640px) {
          .company-profile {
            padding: 100px 0;
          }

          .container {
            padding: 0 20px;
          }

          .title {
            font-size: 2.2rem;
          }

          .stats {
            gap: 24px;
          }

          .stat-number {
            font-size: 1.8rem;
          }

          .logo-container {
            width: 250px;
            height: 250px;
          }

          .circle-1,
          .circle-2,
          .circle-3 {
            display: none;
          }
        }
      `}</style>
    </section>
  );
};

export default CompanyProfile;